import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, X, MessageCircle, Flag, ChevronRight, Sparkles, Image as ImageIcon, Upload, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import ThreadedComments from "@/components/ThreadedComments";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoLight } from "@/components/brand/Logo";

const API = process.env.REACT_APP_BACKEND_URL;
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const CATEGORIES = ["Community Voice", "Career Growth", "HR Strategy", "Leadership", "AI in HR", "Industry Insights", "She Leads"];

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export default function InsightsPage() {
  usePageTitle("Community Insights");
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedComments, setExpandedComments] = useState({});

  const toggleComments = (slug) => setExpandedComments(prev => ({ ...prev, [slug]: !prev[slug] }));

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/insights`);
      setPosts(r.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const reportPost = async (id) => {
    if (!user) { window.location.href = "/login"; return; }
    if (!window.confirm("Report this post for admin review?")) return;
    try {
      await axios.post(`${API}/api/insights/${id}/report`, {}, { withCredentials: true });
      alert("Reported. Thanks for keeping the community safe.");
    } catch (err) { alert(err?.response?.data?.detail || "Could not report"); }
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-white pt-20 pb-4" data-testid="insights-branded-header">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between flex-wrap gap-3">
          <LogoLight width={180} height={42} />
          <span style={{ borderLeft: "3px solid #C9A84C", paddingLeft: "10px", fontFamily: "Georgia,serif", fontSize: "13px", color: "#0F1E35" }}>Insights</span>
        </div>
        <div className="max-w-5xl mx-auto px-4 mt-4" style={{ height: "1px", background: "rgba(15,30,53,0.08)" }} />
      </section>
      <section className="bg-navy pt-12 pb-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-playfair text-white text-4xl sm:text-5xl font-bold mb-4">
            Community Insights
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-dm text-white/70 text-lg mb-8">
            Real stories, hard-won lessons, and honest perspectives from HR practitioners across India and beyond.
          </motion.p>
          {user ? (
            <button onClick={() => setShowForm(true)} data-testid="open-submit-insight"
              className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg flex items-center gap-2 mx-auto hover:bg-gold/90 transition-colors">
              <Plus className="w-4 h-4" /> Share Your Insight
            </button>
          ) : (
            <Link to="/signup" className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg inline-flex items-center gap-2 hover:bg-gold/90">
              <Plus className="w-4 h-4" /> Sign Up to Share
            </Link>
          )}
          <p className="font-dm text-white/40 text-xs mt-4">All submissions reviewed by RoleIQ moderators before publishing</p>
        </div>
      </section>

      {/* Posts */}
      <section className="bg-cream py-16">
        <div className="max-w-5xl mx-auto px-4">
          {loading ? (
            <p className="font-dm text-navy/40 text-sm text-center py-20">Loading insights...</p>
          ) : posts.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-md">
              <Sparkles className="w-10 h-10 text-gold mx-auto mb-3" />
              <h3 className="font-playfair text-navy text-xl font-bold mb-2">Be the first voice in this community</h3>
              <p className="font-dm text-navy/60 text-sm">No insights have been published yet. Share yours and shape the conversation.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {posts.map((p, i) => (
                <motion.div key={p._id} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow overflow-hidden" data-testid={`insight-${p.slug}`}>
                  {p.cover_image && (
                    <img src={p.cover_image.startsWith("http") ? p.cover_image : `${API}${p.cover_image}`} alt="" className="w-full h-44 object-cover" />
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="inline-block bg-gold/10 text-gold font-dm text-xs px-3 py-1 rounded-full">{p.category || "Community Voice"}</span>
                      {p.featured && <span className="inline-block bg-navy text-gold font-dm text-xs px-3 py-1 rounded-full">★ Featured</span>}
                    </div>
                    <h3 className="font-playfair text-navy text-xl font-semibold mb-2">{p.title}</h3>
                    <p className="font-dm text-navy/60 text-sm mb-4 whitespace-pre-wrap">{p.body}</p>
                    <div className="flex items-center justify-between flex-wrap gap-2 text-navy/50 font-dm text-xs">
                      <div>
                        <p className="font-semibold text-navy">{p.author_name}</p>
                        <p className="text-navy/40 text-[10px]">{p.author_role || "Community Member"} · {timeAgo(p.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button onClick={() => toggleComments(p.slug)} data-testid={`toggle-comments-${p.slug}`}
                          className="text-navy/50 hover:text-gold flex items-center gap-1 transition-colors font-semibold">
                          <MessageCircle className="w-3 h-3" /> {p.comment_count || 0} {expandedComments[p.slug] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                        <button onClick={() => reportPost(p._id)} data-testid={`report-${p._id}`}
                          className="text-navy/30 hover:text-red-500 flex items-center gap-1 transition-colors" title="Report this post">
                          <Flag className="w-3 h-3" /> Report
                        </button>
                      </div>
                    </div>
                    {expandedComments[p.slug] && (
                      <div className="mt-4 pt-4 border-t border-navy/10" data-testid={`insight-comments-${p.slug}`}>
                        <ThreadedComments targetType="insight" targetSlug={p.slug} headingClass="font-playfair text-navy text-lg font-semibold mb-4 flex items-center gap-2" />
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-playfair text-white text-3xl font-bold mb-3">Have a story worth sharing?</h2>
          <p className="font-dm text-white/60 mb-6">Submit a 80+ word insight. The strongest pieces get featured on our main blog.</p>
          {user ? (
            <button onClick={() => setShowForm(true)} className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Submit Now</button>
          ) : (
            <Link to="/signup" className="bg-gold text-navy font-dm font-bold px-8 py-3 rounded-lg inline-flex items-center gap-2">Get Started</Link>
          )}
        </div>
      </section>

      {showForm && <SubmitModal onClose={() => setShowForm(false)} onSubmitted={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function SubmitModal({ onClose, onSubmitted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("Community Voice");
  const [tags, setTags] = useState("");
  const [cover, setCover] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/api/insights/upload-cover`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setCover(r.data.url);
    } catch (err) { setError(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setError("");
    if (title.trim().length < 8) { setError("Title must be at least 8 characters"); return; }
    if (body.trim().length < 80) { setError("Body must be at least 80 characters"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/insights`, {
        title: title.trim(),
        body: body.trim(),
        category,
        cover_image: cover,
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
      }, { withCredentials: true });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center" onClick={e => e.stopPropagation()} data-testid="submit-success-modal">
          <Sparkles className="w-12 h-12 text-gold mx-auto mb-4" />
          <h3 className="font-playfair text-navy text-2xl font-bold mb-2">Submitted for review!</h3>
          <p className="font-dm text-navy/60 text-sm mb-6">Our team will review your post and you'll get notified by email + in-app once it's published.</p>
          <button onClick={() => { onSubmitted(); }} className="bg-navy text-gold font-dm font-bold px-8 py-3 rounded-lg" data-testid="success-done">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full my-8" onClick={e => e.stopPropagation()} data-testid="submit-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-navy/10 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-playfair text-navy text-xl font-bold">Share Your Insight</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="font-dm text-navy/70 text-xs font-bold">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value.slice(0, 140))} placeholder="e.g. What I learned running a 5000-person rollout"
              data-testid="insight-title"
              className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
            <p className="font-dm text-navy/40 text-[10px] text-right">{title.length}/140</p>
          </div>
          <div>
            <label className="font-dm text-navy/70 text-xs font-bold">Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="font-dm text-navy/70 text-xs font-bold">Your Story (min 80 characters)</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={8} placeholder="Share what you experienced, learned, or believe..."
              data-testid="insight-body"
              className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm leading-relaxed" />
            <p className="font-dm text-navy/40 text-[10px] text-right">{body.length} chars</p>
          </div>
          <div>
            <label className="font-dm text-navy/70 text-xs font-bold flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Cover Image (optional)</label>
            <div className="flex items-center gap-2 mt-1">
              <input type="file" accept="image/*" onChange={e => uploadCover(e.target.files?.[0])} disabled={uploading}
                data-testid="insight-cover-input"
                className="flex-1 font-dm text-xs file:bg-navy file:text-gold file:font-bold file:border-0 file:px-3 file:py-1.5 file:rounded-lg" />
              {cover && <img src={cover.startsWith("http") ? cover : `${API}${cover}`} alt="" className="w-16 h-12 object-cover rounded border border-navy/10" />}
            </div>
          </div>
          <div>
            <label className="font-dm text-navy/70 text-xs font-bold">Tags (comma separated, optional)</label>
            <input value={tags} onChange={e => setTags(e.target.value)} placeholder="leadership, india, performance"
              className="w-full mt-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
          </div>
          {error && <p className="font-dm text-red-500 text-xs bg-red-50 p-2 rounded-lg" data-testid="submit-error">{error}</p>}
          <p className="font-dm text-navy/50 text-[10px] bg-cream p-3 rounded-lg">
            <strong>Community guidelines:</strong> No promotion or selling. No personal attacks. Posts pass an automated filter and are reviewed by humans before going live.
          </p>
          <button onClick={submit} disabled={submitting} data-testid="insight-submit"
            className="w-full bg-gold text-navy font-dm font-bold py-3 rounded-lg hover:bg-gold/90 disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? "Submitting..." : <><Upload className="w-4 h-4" /> Submit for Review</>}
          </button>
        </div>
      </div>
    </div>
  );
}
